//@flow
import * as React from 'react';

import RouteRestrictionMarker from 'common/components/core/markers/RouteRestrictionMarker';
import RouteRestrictionPolygon from 'common/components/core/map/RouteRestrictionPolygon';
import { Polyline } from 'common/components/core/map/Polyline';
import { PolylineMarker } from 'common/components/core/map/PolylineMarker';
import { MarkerType } from 'common/enums/MapEnum';
import { SegmentMarker } from 'common/components/core/map/SegmentMarker';
import LatLngUtils from 'common/utils/LatLngUtils';
import { dashboardColors } from 'common/enums/ColorEnum';
import { RouteRestriction } from 'common/models/internal/RouteRestriction';
import type { List } from 'immutable';

type Props = {
    routeRestrictions: List<RouteRestriction>,
    showRouteRestrictions: boolean,
    theme: Object,
    children: any,
    getRouteRestrictionsComponentMapRef: () => void,
    ref: () => void,
    onChildMouseDown: () => void
};

type State = {
    mapHelper: ?any,
    activeRouteRestrictionId: ?string,
    hoveredRouteRestrictionId: ?string
};

export const mapWithRouteRestrictions = MapComponent => {
    return class MapWithRouteRestrictions extends React.Component<
        Props,
        State
    > {
        state: State;
        constructor(props: Props) {
            super(props);
            this.state = {
                mapHelper: null,
                activeRouteRestrictionId: null,
                hoveredRouteRestrictionId: null
            };
        }

        onMapLoad = ({ map, maps }) => {
            this.setState({ mapHelper: { map, maps } });
            this.props.onGoogleApiLoaded &&
                this.props.onGoogleApiLoaded({ map, maps });
        };

        getRouteRestrictionsComponentMapRef = mapRef => {
            const { getRouteRestrictionsComponentMapRef, ref } = this.props;
            this.map = mapRef;
            getRouteRestrictionsComponentMapRef &&
                getRouteRestrictionsComponentMapRef(mapRef);
            ref && ref(mapRef);
        };

        onRouteRestrictionClick = (routeRestrictionId: string) => {
            const { mapHelper, activeRouteRestrictionId } = this.state;
            const { routeRestrictions } = this.props;
            const clickedRouteRestriction =
                routeRestrictions &&
                routeRestrictions.find(
                    routeRestriction =>
                        routeRestriction.id === routeRestrictionId
                );
            if (!clickedRouteRestriction) {
                return;
            }
            const { center } = clickedRouteRestriction;
            const centerLatLng =
                center &&
                new google.maps.LatLng({
                    lat: center.lat,
                    lng: center.lng
                });
            if (
                mapHelper &&
                routeRestrictionId &&
                routeRestrictionId !== activeRouteRestrictionId
            ) {
                this.setState({
                    activeRouteRestrictionId: routeRestrictionId
                });
                const polylineBounds = this.getPolylineBoundsFromRouteRestrictions(
                    clickedRouteRestriction
                );
                if (polylineBounds) {
                    // zooming route restriction into the view (polyline)
                    mapHelper.map.fitBounds(polylineBounds);
                } else {
                    // zooming route restriction into the view (polygon)
                    if (mapHelper.map.getZoom() < 15) {
                        mapHelper.map.setZoom(15);
                    }
                    mapHelper.map.panTo(centerLatLng);
                }
            } else if (
                !routeRestrictionId ||
                routeRestrictionId === activeRouteRestrictionId //toggle
            ) {
                this.setState({ activeRouteRestrictionId: null });
            }
        };

        getPolylineBoundsFromRouteRestrictions = (
            activeRouteRestriction: string
        ) => {
            if (activeRouteRestriction && activeRouteRestriction.polyline) {
                return LatLngUtils.getBoundsFromPolyline(
                    activeRouteRestriction.polyline
                );
            }
        };

        getDataForPolyline = (polyline: string) => {
            const decodedPolyline = google.maps.geometry.encoding.decodePath(
                polyline
            );
            const [startLatLng] = decodedPolyline;
            const [endLatLng] = decodedPolyline.slice(-1);
            return {
                decodedPolyline,
                startLatLng,
                endLatLng
            };
        };

        getRouteRestrictions = (
            routeRestrictions: List<RouteRestriction>,
            activeRouteRestrictionId: ?string,
            hoveredRouteRestrictionId: ?string
        ) => {
            const { mapHelper } = this.state;
            const routeRestrictionsMapItems = [];
            routeRestrictions &&
                routeRestrictions.forEach(routeRestriction => {
                    const {
                        polygon,
                        polyline,
                        center,
                        lineSegments
                    } = routeRestriction;
                    const { lat, lng } = center;
                    let polygons;
                    if (polygon) {
                        ({ polygons } = polygon);
                    }

                    if (lat && lng) {
                        routeRestrictionsMapItems.push(
                            <RouteRestrictionMarker
                                markerType={MarkerType.HOVERABLE}
                                onPopupCloseClick={this.onPopupClose}
                                isPopupOpen={
                                    activeRouteRestrictionId ===
                                    routeRestriction.id
                                }
                                isSelected={
                                    activeRouteRestrictionId ===
                                    routeRestriction.id
                                }
                                lat={lat}
                                lng={lng}
                                onHover={this.onRouteRestrictionCenterHover}
                                onHoverOut={
                                    this.onRouteRestrictionCenterHoverOut
                                }
                                routeRestrictionId={routeRestriction.id}
                                key={routeRestriction.id}
                                routeRestriction={routeRestriction}
                                sideLength={24}
                            />
                        );
                    }
                    const isRouteRestrictionActive =
                        hoveredRouteRestrictionId === routeRestriction.id ||
                        activeRouteRestrictionId === routeRestriction.id;

                    // polygon / region block
                    if (
                        polygons &&
                        polygons.size > 0 &&
                        mapHelper &&
                        lat &&
                        lng &&
                        isRouteRestrictionActive
                    ) {
                        routeRestrictionsMapItems.push(
                            <RouteRestrictionPolygon
                                isActive={isRouteRestrictionActive}
                                routeRestrictionId={routeRestriction.id}
                                color={dashboardColors.salmon.code}
                                theme={this.props.theme}
                                mapHelper={mapHelper}
                                key={`${routeRestriction.id}–polygon`}
                                routeRestriction={routeRestriction}
                            />
                        );
                    }

                    // polyline / road block
                    if (
                        polyline &&
                        mapHelper &&
                        lat &&
                        lng &&
                        isRouteRestrictionActive
                    ) {
                        const {
                            decodedPolyline,
                            startLatLng,
                            endLatLng
                        } = this.getDataForPolyline(polyline);
                        routeRestrictionsMapItems.push(
                            <Polyline
                                zIndex={10}
                                key={`${routeRestriction.id}-polyline`}
                                mapHelper={mapHelper}
                                path={decodedPolyline}
                                color={dashboardColors.salmon.code}
                            />
                        );

                        routeRestrictionsMapItems.push(
                            <PolylineMarker
                                markerType={MarkerType.NON_HOVERABLE}
                                key={`${routeRestriction.id}-polyline-marker-start`}
                                markerIcon={'A'}
                                lat={startLatLng.lat()}
                                lng={startLatLng.lng()}
                            />
                        );

                        routeRestrictionsMapItems.push(
                            <PolylineMarker
                                markerType={MarkerType.NON_HOVERABLE}
                                key={`${routeRestriction.id}-polyline-marker-end`}
                                markerIcon={'B'}
                                lat={endLatLng.lat()}
                                lng={endLatLng.lng()}
                            />
                        );
                    }

                    // line segments / divide block

                    if (
                        lineSegments &&
                        mapHelper &&
                        lat &&
                        lng &&
                        isRouteRestrictionActive
                    ) {
                        const { segments } = lineSegments;
                        segments.forEach((segment, index) => {
                            const { latLng1, latLng2 } = segment;
                            const startLatLng = new google.maps.LatLng({
                                lat: latLng1.lat,
                                lng: latLng1.lng
                            });

                            const endLatLng = new google.maps.LatLng({
                                lat: latLng2.lat,
                                lng: latLng2.lng
                            });

                            const segmentPolyline = [startLatLng, endLatLng];
                            routeRestrictionsMapItems.push(
                                <Polyline
                                    zIndex={10}
                                    mapHelper={mapHelper}
                                    key={`${routeRestriction.id}-segment-${index}`}
                                    path={segmentPolyline}
                                    color={dashboardColors.salmon.code}
                                />
                            );
                            const prevSegment =
                                index && segments.get(index - 1);
                            const nextSegment = segments.get(index + 1);
                            if (
                                !prevSegment ||
                                !prevSegment.latLng2.equals(latLng1) // prev end is equal to current start
                            ) {
                                routeRestrictionsMapItems.push(
                                    <SegmentMarker
                                        markerType={MarkerType.NON_HOVERABLE}
                                        key={`${routeRestriction.id}-segment-${index}-start`}
                                        lat={startLatLng.lat()}
                                        lng={startLatLng.lng()}
                                    />
                                );
                            }
                            if (
                                !nextSegment ||
                                !nextSegment.latLng1.equals(latLng2) // current end is not equal to next start
                            ) {
                                routeRestrictionsMapItems.push(
                                    <SegmentMarker
                                        markerType={MarkerType.NON_HOVERABLE}
                                        key={`${routeRestriction.id}-segment-${index}-end`}
                                        lat={endLatLng.lat()}
                                        lng={endLatLng.lng()}
                                    />
                                );
                            }
                        });
                    }
                });
            return routeRestrictionsMapItems;
        };

        onChildMouseDown = (key: string, ...args) => {
            const { onChildMouseDown } = this.props;
            this.onRouteRestrictionClick(key);
            onChildMouseDown && onChildMouseDown(key, ...args);
        };

        onRouteRestrictionCenterHover = (key: string) => {
            this.setState({ hoveredRouteRestrictionId: key });
        };

        onRouteRestrictionCenterHoverOut = (key: string) => {
            // Using functional setState to handle batched updates to state (by using prevvState)
            this.setState(prevState => {
                if (prevState.hoveredRouteRestrictionId === key) {
                    return {
                        hoveredRouteRestrictionId: null
                    };
                }
            });
        };

        onPopupClose = (key: string) => {
            if (key === this.state.activeRouteRestrictionId) {
                this.onRouteRestrictionClick(key);
            }
        };

        render() {
            const {
                children,
                routeRestrictions,
                showRouteRestrictions,
                getRouteRestrictionsComponentMapRef, //eslint-disable-line no-unused-vars
                ...others
            } = this.props;
            const {
                activeRouteRestrictionId,
                hoveredRouteRestrictionId
            } = this.state;
            return (
                <MapComponent
                    {...others}
                    ref={this.getRouteRestrictionsComponentMapRef}
                    onGoogleApiLoaded={this.onMapLoad}
                    yesIWantToUseGoogleMapApiInternals
                    onChildMouseDown={this.onChildMouseDown}
                >
                    {children}
                    {showRouteRestrictions
                        ? this.getRouteRestrictions(
                              routeRestrictions,
                              activeRouteRestrictionId,
                              hoveredRouteRestrictionId
                          )
                        : []}
                </MapComponent>
            );
        }
    };
};

export default mapWithRouteRestrictions;
